import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

/**
 * Premium custom dropdown for the admin panel.
 * Supports:
 *  - Styled trigger matching the form-field input theme
 *  - Portal-based dropdown for z-index safety
 *  - Optional search filtering (great for fonts, occupations, etc.)
 *  - Keyboard navigation (arrow keys + enter + escape)
 *  - Max-height scrollable list
 *
 * Props:
 *   value        - currently selected value
 *   onChange      - (value) => void
 *   options       - [{ value, label, hint? }] or ["string", ...]
 *   placeholder   - text when nothing is selected
 *   searchable    - show a search box
 *   searchPlaceholder - placeholder for the search input
 *   disabled      - disable the dropdown
 */
export default function AdminSelect({
  value,
  onChange,
  options = [],
  placeholder = "Select...",
  searchable = false,
  searchPlaceholder = "Search...",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [dropPos, setDropPos] = useState(null);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const btnRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);

  // Normalize options to { value, label, hint? }
  const normalized = options.map((o) =>
    typeof o === "string" ? { value: o, label: o } : o
  );

  const chosen = normalized.find((o) => String(o.value) === String(value));

  const filtered = searchable && search
    ? normalized.filter((o) =>
        o.label.toLowerCase().includes(search.toLowerCase())
      )
    : normalized;

  // Position the dropdown
  const openDropdown = () => {
    if (disabled) return;
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - r.bottom;
      const dropUp = spaceBelow < 260 && r.top > 260;
      setDropPos({
        top: dropUp ? undefined : r.bottom + 4,
        bottom: dropUp ? window.innerHeight - r.top + 4 : undefined,
        left: r.left,
        width: r.width,
      });
    }
    setHighlightIdx(-1);
    setOpen(true);
  };

  // Close on outside click / scroll
  useEffect(() => {
    if (!open) { setSearch(""); return; }
    const onDown = (e) => {
      if (!btnRef.current?.contains(e.target)) setOpen(false);
    };
    const onScroll = () => setOpen(false);
    document.addEventListener("mousedown", onDown);
    window.addEventListener("scroll", onScroll, { passive: true });
    if (searchable) setTimeout(() => searchRef.current?.focus(), 30);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("scroll", onScroll);
    };
  }, [open, searchable]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightIdx >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll(".admin-select-opt");
      items[highlightIdx]?.scrollIntoView({ block: "nearest" });
    }
  }, [highlightIdx]);

  const handleSelect = (v) => {
    onChange(v);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openDropdown();
      }
      return;
    }
    if (e.key === "Escape") { setOpen(false); return; }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIdx((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && highlightIdx >= 0) {
      e.preventDefault();
      handleSelect(filtered[highlightIdx].value);
    }
  };

  return (
    <div className={`admin-select${open ? " open" : ""}${disabled ? " disabled" : ""}`}>
      <button
        ref={btnRef}
        type="button"
        className={`admin-select-trigger${!chosen ? " placeholder" : ""}`}
        onClick={() => (open ? setOpen(false) : openDropdown())}
        onKeyDown={onKeyDown}
        disabled={disabled}
        tabIndex={0}
      >
        <span className="admin-select-value">{chosen ? chosen.label : placeholder}</span>
        <svg className={`admin-select-chevron${open ? " open" : ""}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && dropPos && createPortal(
        <div
          className="admin-select-dropdown"
          ref={listRef}
          style={{
            position: "fixed",
            top: dropPos.top,
            bottom: dropPos.bottom,
            left: dropPos.left,
            width: dropPos.width,
            zIndex: 99999,
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onKeyDown={onKeyDown}
        >
          {searchable && (
            <div className="admin-select-search">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input
                ref={searchRef}
                type="text"
                placeholder={searchPlaceholder}
                value={search}
                onChange={(e) => { setSearch(e.target.value); setHighlightIdx(0); }}
                onKeyDown={onKeyDown}
              />
            </div>
          )}
          <div className="admin-select-options">
            {filtered.length === 0 && (
              <div className="admin-select-empty">No results found</div>
            )}
            {filtered.map((o, idx) => (
              <div
                key={o.value}
                className={`admin-select-opt${String(o.value) === String(value) ? " selected" : ""}${idx === highlightIdx ? " highlighted" : ""}`}
                onMouseDown={() => handleSelect(o.value)}
                onMouseEnter={() => setHighlightIdx(idx)}
              >
                <span>{o.label}</span>
                {o.hint && <span className="admin-select-hint">{o.hint}</span>}
                {String(o.value) === String(value) && (
                  <svg className="admin-select-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="14" height="14"><polyline points="20 6 9 17 4 12" /></svg>
                )}
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
